import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Mail } from "lucide-react";

export const STAGES = [
  { id: "new", label: "New", color: "#E5C17E" },
  { id: "contacted", label: "Contacted", color: "#7FA8B0" },
  { id: "qualified", label: "Qualified", color: "#8DBFA1" },
  { id: "meeting_booked", label: "Meeting Booked", color: "#9DAFE0" },
  { id: "proposal_sent", label: "Proposal Sent", color: "#C9A8E0" },
  { id: "won", label: "Won", color: "#7FB08A" },
  { id: "lost", label: "Lost", color: "#9C7373" },
  { id: "unreachable", label: "Unreachable", color: "#B8774A" },
];

const fmtEur = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n)
    : null;

const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });

export default function LeadKanban({ leads, onMove, onOpen }) {
  // Group by stage
  const byStage = STAGES.reduce((acc, s) => ({ ...acc, [s.id]: [] }), {});
  leads.forEach((l) => {
    const s = l.stage || l.status || "new";
    if (byStage[s]) byStage[s].push(l);
    else byStage.new.push(l);
  });

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { draggableId, destination, source } = result;
    if (source.droppableId === destination.droppableId) return;
    await onMove(draggableId, destination.droppableId);
  };

  const totalPipelineValue = leads
    .filter((l) => !["won", "lost", "unreachable"].includes(l.stage || l.status || "new"))
    .reduce((sum, l) => sum + (l.deal_value_eur || 0), 0);
  const wonValue = leads
    .filter((l) => (l.stage || l.status) === "won")
    .reduce((sum, l) => sum + (l.deal_value_eur || 0), 0);

  return (
    <div data-testid="lead-kanban">
      {/* Pipeline summary */}
      <div
        className="mb-6 px-6 py-5 flex flex-wrap items-center gap-8 rounded-md"
        style={{ background: "rgba(190,170,140,0.04)", border: "1px solid var(--line-2)" }}
        data-testid="kanban-summary"
      >
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--mute-2)]">
            Open pipeline
          </div>
          <div className="display text-2xl text-[var(--bone)] mt-1">
            {fmtEur(totalPipelineValue) || "—"}
          </div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--mute-2)]">Won</div>
          <div className="display text-2xl text-[var(--bone)] mt-1" style={{ color: "#7FB08A" }}>
            {fmtEur(wonValue) || "—"}
          </div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--mute-2)]">Total leads</div>
          <div className="display text-2xl text-[var(--bone)] mt-1">{leads.length}</div>
        </div>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3 min-w-max">
            {STAGES.map((stage) => {
              const items = byStage[stage.id];
              const stageValue = items.reduce((s, l) => s + (l.deal_value_eur || 0), 0);
              return (
                <Droppable droppableId={stage.id} key={stage.id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      data-testid={`kanban-col-${stage.id}`}
                      className="flex flex-col"
                      style={{
                        width: 280,
                        minHeight: 400,
                        background: snapshot.isDraggingOver
                          ? "rgba(190,170,140,0.06)"
                          : "rgba(255,255,255,0.015)",
                        border: "1px solid var(--line)",
                        borderRadius: 6,
                        transition: "background 0.2s",
                      }}
                    >
                      <div
                        className="px-3 py-3 flex items-center justify-between"
                        style={{ borderBottom: "1px solid var(--line)" }}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 999,
                              background: stage.color,
                              display: "inline-block",
                            }}
                          />
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--bone)]">
                            {stage.label}
                          </span>
                          <span className="font-mono text-[10px] text-[var(--mute-2)]">{items.length}</span>
                        </div>
                        {stageValue > 0 && (
                          <span className="font-mono text-[10px] text-[var(--pewter)]">{fmtEur(stageValue)}</span>
                        )}
                      </div>
                      <div className="p-2 flex-1 space-y-2">
                        {items.map((lead, idx) => (
                          <Draggable draggableId={lead.id} index={idx} key={lead.id}>
                            {(prov, snap) => (
                              <div
                                ref={prov.innerRef}
                                {...prov.draggableProps}
                                {...prov.dragHandleProps}
                                data-testid={`lead-card-${lead.id}`}
                                onClick={() => onOpen(lead)}
                                className="p-3 cursor-pointer group"
                                style={{
                                  background: snap.isDragging
                                    ? "rgba(190,170,140,0.12)"
                                    : "rgba(255,255,255,0.025)",
                                  border: "1px solid var(--line-2)",
                                  borderRadius: 4,
                                  ...prov.draggableProps.style,
                                }}
                              >
                                <div className="text-[var(--bone)] text-sm leading-tight font-medium group-hover:text-[var(--pewter)] transition-colors">
                                  {lead.first_name} {lead.last_name}
                                </div>
                                {lead.company && (
                                  <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--mute-2)] mt-1">
                                    {lead.company}
                                  </div>
                                )}
                                <div className="mt-2.5 flex items-center justify-between gap-2 flex-wrap">
                                  <a
                                    href={`mailto:${lead.email}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-[var(--mute)] hover:text-[var(--pewter)]"
                                    title={lead.email}
                                  >
                                    <Mail size={12} />
                                  </a>
                                  <div className="flex items-center gap-2">
                                    {lead.deal_value_eur ? (
                                      <span className="font-mono text-[10px] text-[var(--pewter)]">
                                        {fmtEur(lead.deal_value_eur)}
                                      </span>
                                    ) : null}
                                    <span className="font-mono text-[9px] text-[var(--mute-2)]">
                                      {(lead.language || "en").toUpperCase()}
                                    </span>
                                    <span className="font-mono text-[9px] text-[var(--mute-2)]">
                                      {fmtDate(lead.created_at)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {items.length === 0 && (
                          <div className="text-center text-[var(--mute-2)] font-mono text-[10px] uppercase tracking-[0.2em] py-6">
                            —
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </div>
      </DragDropContext>
    </div>
  );
}
